// Admin "Clip Quality" tab — the human half of the eval-driven data engine.
// Label shipped clips good/bad (with WHY + the cut they should have had), log
// good moments the pipeline missed, and watch quality over time + every eval
// run scored against these labels (clip-engine-runpod/eval_engine.py).
// Backend: routes/adminEval.js. Labels are regions of the SOURCE video, so they
// stay valid for every future pipeline version.
//
// Keys (Label view): G good · B bad · 1–9,0,-,= toggle reasons · Enter save ·
// ←/→ prev/next clip · Space play/pause · M add missed moment
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { axiosInstance } from "../../config";
import toast from "react-hot-toast";
import { EVAL_FAILURES, FAILURE_GROUPS, GENRES, genreLabel } from "./evalFailures";
import "./clipQuality.css";
import { jobDisplayTitle } from "../../utils/jobTitle";

const REASON_KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0", "-", "=", "["];

function mmss(sec) {
  if (sec == null || isNaN(sec)) return "—";
  const s = Math.max(0, Math.round(sec));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), r = s % 60;
  return h ? `${h}:${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}` : `${m}:${String(r).padStart(2, "0")}`;
}

function parseTime(str) {
  const parts = String(str || "").trim().split(":").map(Number);
  if (!parts.length || parts.some((p) => isNaN(p))) return null;
  return parts.reduce((acc, p) => acc * 60 + p, 0);
}

// Link to the source VOD at a timestamp, so missed moments / ideal cuts can be checked.
function sourceAt(url, sec) {
  if (!url) return null;
  const s = Math.max(0, Math.floor(sec || 0));
  if (/twitch\.tv/.test(url)) {
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), r = s % 60;
    return `${url.split("?")[0]}?t=${h}h${m}m${r}s`;
  }
  if (/youtu/.test(url)) return `${url}${url.includes("?") ? "&" : "?"}t=${s}`;
  return url;
}

const pct = (v) => (v == null ? "—" : `${Math.round(v * 100)}%`);

export default function ClipQualityTab() {
  const [view, setView] = useState("label");
  return (
    <div className="admindash-section admindash-fade-in cq">
      <div className="ref-head">
        <div>
          <h2 className="admindash-section-title" style={{ margin: 0 }}>Clip quality</h2>
          <div className="muted" style={{ fontSize: 13 }}>
            Every clip you judge becomes a test case. No pipeline change ships unless precision (quality) AND recall (quantity) hold.
          </div>
        </div>
      </div>
      <div className="ref-filters">
        {[["label", "Label clips"], ["paste", "Paste links"], ["quality", "Quality & eval runs"]].map(([k, l]) => (
          <button key={k} className={`ref-pill ${view === k ? "active" : ""}`} onClick={() => setView(k)}>{l}</button>
        ))}
      </div>
      {view === "label" && <LabelView />}
      {view === "paste" && <PasteView onOpen={() => setView("label")} />}
      {view === "quality" && <QualityView />}
    </div>
  );
}

/* ── Label ─────────────────────────────────────────────────────────────── */

// PasteView hands a batch to LabelView through here (both mount fresh).
let pendingOpen = null;

function LabelView() {
  const [jobs, setJobs] = useState(null);
  const [jobId, setJobId] = useState(pendingOpen?.jobId || null);
  const [detail, setDetail] = useState(null);
  const [idx, setIdx] = useState(0);
  const openClipRef = useRef(pendingOpen?.clipId || null);

  const loadJobs = useCallback(async () => {
    try {
      const res = await axiosInstance.get("/admin/eval/jobs?limit=60");
      setJobs(res.data.jobs);
    } catch (e) {
      toast.error(e.response?.data?.error || "Failed to load jobs");
    }
  }, []);

  const loadJob = useCallback(async (id) => {
    try {
      const res = await axiosInstance.get(`/admin/eval/jobs/${id}`);
      setDetail(res.data);
      return res.data;
    } catch (e) {
      toast.error(e.response?.data?.error || "Failed to load job");
      return null;
    }
  }, []);

  useEffect(() => { loadJobs(); pendingOpen = null; }, [loadJobs]);

  useEffect(() => {
    if (!jobId) return;
    loadJob(jobId).then((d) => {
      if (!d) return;
      const want = openClipRef.current;
      openClipRef.current = null;
      const labeled = new Set(d.labels.filter((l) => l.origin === "shipped").map((l) => String(l.clipId)));
      const i = want ? d.job.clips.findIndex((c) => String(c._id) === String(want))
        : d.job.clips.findIndex((c) => !labeled.has(String(c._id)));
      setIdx(Math.max(0, i));
    });
  }, [jobId, loadJob]);

  const refresh = async () => {
    await loadJob(jobId);
    loadJobs();
  };

  return (
    <div className="cq-label">
      <div className="cq-jobs">
        {!jobs ? <div className="admindash-empty admindash-shimmer">Loading…</div> : jobs.map((j) => (
          <button key={j._id} className={`cq-job ${jobId === j._id ? "active" : ""}`} onClick={() => setJobId(j._id)}>
            <div className="cq-job-title">{jobDisplayTitle(j)}</div>
            <div className="cq-job-meta">
              <span>{j.streamerName || j.platform}</span>
              <span>· {new Date(j.createdAt).toLocaleDateString()}</span>
            </div>
            <div className="cq-job-meta">
              <span className={j.labeled >= j.clips ? "cq-ok" : ""}>{j.labeled}/{j.clips} labeled</span>
              {j.bad > 0 && <span className="cq-bad">· {j.bad} bad</span>}
              {j.fullyLabeled && <span className="cq-ok">· done</span>}
              {j.frozen && <span className="muted">· frozen</span>}
              {j.genre ? <span className="muted">· {genreLabel(j.genre)}</span> : j.labeled > 0 && <span className="cq-bad">· pick genre</span>}
              {j.split === "holdout" && <span className="muted" title="Holdout: only used for the final pre-ship check">· 🔒</span>}
            </div>
          </button>
        ))}
      </div>
      <div className="cq-main">
        {!detail ? (
          <div className="admindash-empty">Pick a job on the left. Label every clip, then add any good moments it missed.</div>
        ) : (
          <JobLabeler key={detail.job._id} detail={detail} idx={idx} setIdx={setIdx} onSaved={refresh} />
        )}
      </div>
    </div>
  );
}

function JobLabeler({ detail, idx, setIdx, onSaved }) {
  const { job, labels, source } = detail;
  const clips = job.clips;
  const clip = clips[idx];
  const labelByClip = useMemo(
    () => Object.fromEntries(labels.filter((l) => l.origin === "shipped").map((l) => [String(l.clipId), l])),
    [labels]
  );
  const missed = labels.filter((l) => l.origin === "missed");
  const existing = clip ? labelByClip[String(clip._id)] : null;

  const [verdict, setVerdict] = useState(null);
  const [failures, setFailures] = useState([]);
  const [startDelta, setStartDelta] = useState(0);
  const [endDelta, setEndDelta] = useState(0);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [showMissed, setShowMissed] = useState(false);
  const videoRef = useRef(null);

  // Load the form from this clip's existing label (or blank).
  useEffect(() => {
    setVerdict(existing?.verdict || null);
    setFailures(existing?.failures || []);
    setStartDelta(existing?.idealStart != null ? +(existing.idealStart - clip.start_sec).toFixed(1) : 0);
    setEndDelta(existing?.idealEnd != null ? +(existing.idealEnd - clip.end_sec).toFixed(1) : 0);
    setNote(existing?.note || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clip?._id, existing?._id, existing?.updatedAt]);

  const needsCut = failures.some((f) => EVAL_FAILURES.find((x) => x.key === f)?.group === "cut");

  const toggleFailure = (key) => {
    setVerdict("bad");
    setFailures((fs) => (fs.includes(key) ? fs.filter((f) => f !== key) : [...fs, key]));
  };

  const next = useCallback(() => setIdx((i) => Math.min(clips.length - 1, i + 1)), [clips.length, setIdx]);
  const prev = useCallback(() => setIdx((i) => Math.max(0, i - 1)), [setIdx]);

  const save = useCallback(async (forced) => {
    const v = forced || verdict;
    if (!v || !clip) return toast.error("Pick Good (G) or Bad (B)");
    if (v === "bad" && !failures.length) return toast.error("Say why it's bad — pick at least one reason");
    const hasCut = v === "bad" && (startDelta !== 0 || endDelta !== 0);
    setSaving(true);
    try {
      await axiosInstance.post("/admin/eval/labels", {
        jobId: job._id, clipId: clip._id, verdict: v, failures: v === "bad" ? failures : [], note,
        idealStart: hasCut ? +(clip.start_sec + startDelta).toFixed(2) : null,
        idealEnd: hasCut ? +(clip.end_sec + endDelta).toFixed(2) : null,
      });
      toast.success(v === "good" ? "Good ✓" : "Bad ✗", { duration: 900 });
      await onSaved();
      next();
    } catch (e) {
      toast.error(e.response?.data?.error || "Save failed");
    } finally {
      setSaving(false);
    }
  }, [verdict, failures, startDelta, endDelta, note, clip, job._id, onSaved, next]);

  useEffect(() => {
    const onKey = (e) => {
      if (["INPUT", "TEXTAREA", "SELECT"].includes(e.target.tagName)) return;
      const k = e.key;
      if (k === "g" || k === "G") { setVerdict("good"); setFailures([]); save("good"); }
      else if (k === "b" || k === "B") setVerdict("bad");
      else if (k === "Enter") save();
      else if (k === "ArrowRight") next();
      else if (k === "ArrowLeft") prev();
      else if (k === "m" || k === "M") setShowMissed(true);
      else if (k === " ") {
        e.preventDefault();
        const v = videoRef.current;
        if (v) v.paused ? v.play() : v.pause();
      } else {
        const i = REASON_KEYS.indexOf(k);
        if (i >= 0 && EVAL_FAILURES[i]) toggleFailure(EVAL_FAILURES[i].key);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [save, next, prev]);

  const toggleFull = async () => {
    try {
      await axiosInstance.post(`/admin/eval/sources/${encodeURIComponent(job.sourceKey)}/fully-labeled`, { value: !source?.fullyLabeled });
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.error || "Couldn't update");
    }
  };

  const setGenre = async (genre) => {
    try {
      await axiosInstance.post(`/admin/eval/sources/${encodeURIComponent(job.sourceKey)}/genre`, { genre });
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.error || "Couldn't set genre");
    }
  };

  const delMissed = async (id) => {
    await axiosInstance.delete(`/admin/eval/labels/${id}`);
    onSaved();
  };

  if (!clip) return <div className="admindash-empty">This job has no clips.</div>;
  const allLabeled = clips.every((c) => labelByClip[String(c._id)]);

  return (
    <div>
      <div className="cq-jobhead">
        <div className="cq-jobhead-info">
          <a href={job.videoUrl} target="_blank" rel="noreferrer" className="ref-handle cq-jobhead-title" title={job.videoUrl}>{jobDisplayTitle(job)}</a>
          <div className="muted cq-jobhead-meta">
            {job.streamerName} · {job.aspectRatio} · source {mmss(job.videoDuration)} · <code title={job.sourceKey}>{job.sourceKey}</code>
          </div>
        </div>
        <div className="cq-headctl">
          <select className="cq-genre" value={source?.genre || ""} onChange={(e) => setGenre(e.target.value)} disabled={!source}
            title={source ? "What kind of stream this is — quality is scored per genre" : "Label a clip first"}>
            <option value="" disabled>Genre…</option>
            {GENRES.map((g) => <option key={g.key} value={g.key}>{g.label}</option>)}
          </select>
          {source?.split === "holdout" && <span className="cq-holdout" title="Locked test VOD: never tuned on, only used for the final check before a change ships. Label it exactly like the others.">🔒 Holdout</span>}
          <label className="cq-full" title="Every shipped clip is judged AND you looked for missed moments. Recall is only computed on finished sources.">
            <input type="checkbox" checked={!!source?.fullyLabeled} onChange={toggleFull} disabled={!source || !source.genre} />
            Source fully labeled
          </label>
        </div>
      </div>

      {source?.freezeError && (
        <div className="ref-warn">⚠️ Not used in evals: {source.freezeError}. Labels here still count toward production precision.</div>
      )}
      <div className="cq-strip">
        {clips.map((c, i) => {
          const l = labelByClip[String(c._id)];
          return (
            <button key={c._id} className={`cq-chip ${i === idx ? "active" : ""} ${l ? `cq-chip--${l.verdict}` : ""}`}
              onClick={() => setIdx(i)} title={c.hook}>
              {i + 1}
            </button>
          );
        })}
        <span className="muted" style={{ fontSize: 12, marginLeft: 8 }}>
          {Object.keys(labelByClip).length}/{clips.length} labeled{allLabeled && !source?.fullyLabeled ? " — add missed moments, then tick “fully labeled”" : ""}
        </span>
      </div>

      <div className="cq-clip">
        <div className="cq-player">
          <video ref={videoRef} key={clip._id} src={clip.url} poster={clip.thumbnailUrl} controls autoPlay playsInline />
        </div>
        <div className="cq-form">
          <div className="cq-hook">{clip.hook || <span className="muted">(no title)</span>}</div>
          <div className="muted" style={{ fontSize: 12 }}>
            Source {mmss(clip.start_sec)}–{mmss(clip.end_sec)} · {Math.round(clip.duration || clip.end_sec - clip.start_sec)}s
            {clip.provenance?.selectionVersion && ` · ${clip.provenance.selectionVersion}`}
            {clip.provenance?.viralScore != null && ` · judge ${clip.provenance.viralScore}/10`}
            {clip.provenance?.verifyScore != null && ` · review ${clip.provenance.verifyScore}/10`}
            {" · "}<a href={sourceAt(job.videoUrl, clip.start_sec - 15)} target="_blank" rel="noreferrer">open source −15s</a>
          </div>
          {clip.caption && <div className="cq-caption">{clip.caption}</div>}

          <div className="cq-verdict">
            <button className={`cq-v cq-v--good ${verdict === "good" ? "on" : ""}`} onClick={() => { setVerdict("good"); setFailures([]); }}>
              Good <kbd>G</kbd>
            </button>
            <button className={`cq-v cq-v--bad ${verdict === "bad" ? "on" : ""}`} onClick={() => setVerdict("bad")}>
              Bad <kbd>B</kbd>
            </button>
          </div>

          {verdict === "bad" && (
            <div className="cq-reasons">
              {FAILURE_GROUPS.map((g) => (
                <div key={g.key} className="cq-group">
                  <div className="cq-group-label">{g.label}</div>
                  {EVAL_FAILURES.map((f, i) => f.group === g.key && (
                    <button key={f.key} className={`cq-reason ${failures.includes(f.key) ? "on" : ""}`} onClick={() => toggleFailure(f.key)}>
                      <kbd>{REASON_KEYS[i]}</kbd> {f.label}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          )}

          {verdict === "bad" && needsCut && (
            <div className="cq-cut">
              <div className="cq-group-label">Where should it have been cut? (seconds vs. this clip — optional, but it turns this into a precise test)</div>
              <CutStepper label="Start" value={startDelta} onChange={setStartDelta} at={clip.start_sec + startDelta} />
              <CutStepper label="End" value={endDelta} onChange={setEndDelta} at={clip.end_sec + endDelta} />
              <div className="muted" style={{ fontSize: 12 }}>
                Ideal: {mmss(clip.start_sec + startDelta)}–{mmss(clip.end_sec + endDelta)} ·{" "}
                <a href={sourceAt(job.videoUrl, clip.start_sec + startDelta - 5)} target="_blank" rel="noreferrer">check in source</a>
              </div>
            </div>
          )}

          <textarea className="cq-note" placeholder="Note (optional) — what a great version of this clip looks like" value={note}
            onChange={(e) => setNote(e.target.value)} rows={2} />

          <div className="ref-actions" style={{ display: "flex", gap: 8 }}>
            <button className="ref-btn ref-btn--primary" disabled={saving} onClick={() => save()}>Save &amp; next <kbd>Enter</kbd></button>
            <button className="ref-btn" onClick={prev} disabled={idx === 0}>← Prev</button>
            <button className="ref-btn" onClick={next} disabled={idx === clips.length - 1}>Next →</button>
          </div>

          {clip.transcript && (
            <details className="cq-transcript">
              <summary>Transcript</summary>
              <div>{clip.transcript}</div>
            </details>
          )}
        </div>
      </div>

      <div className="cq-missed">
        <div className="cq-missed-head">
          <div>
            <strong>Missed moments</strong>
            <span className="muted" style={{ fontSize: 12 }}> — good clips in this source that the pipeline didn't ship (this is how recall is measured)</span>
          </div>
          <button className="ref-btn ref-btn--sm" onClick={() => setShowMissed((s) => !s)}>+ Add missed moment <kbd>M</kbd></button>
        </div>
        {showMissed && <MissedForm job={job} onDone={() => { setShowMissed(false); onSaved(); }} />}
        {missed.length === 0 ? <div className="muted" style={{ fontSize: 13 }}>None logged.</div> : (
          <ul className="cq-missed-list">
            {missed.map((m) => (
              <li key={m._id}>
                <a href={sourceAt(job.videoUrl, m.start_sec)} target="_blank" rel="noreferrer">{mmss(m.start_sec)}–{mmss(m.end_sec)}</a>
                {m.note && <span className="muted"> · {m.note}</span>}
                <button className="cq-x" onClick={() => delMissed(m._id)} title="Delete">×</button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function CutStepper({ label, value, onChange, at }) {
  const step = (d) => onChange(+(value + d).toFixed(1));
  return (
    <div className="cq-stepper">
      <span className="cq-stepper-label">{label}</span>
      {[-5, -1, -0.5].map((d) => <button key={d} className="ref-btn ref-btn--sm" onClick={() => step(d)}>{d}s</button>)}
      <span className="cq-stepper-val">{value > 0 ? "+" : ""}{value}s <span className="muted">({mmss(at)})</span></span>
      {[0.5, 1, 5].map((d) => <button key={d} className="ref-btn ref-btn--sm" onClick={() => step(d)}>+{d}s</button>)}
      {value !== 0 && <button className="ref-btn ref-btn--sm ref-btn--ghost" onClick={() => onChange(0)}>reset</button>}
    </div>
  );
}

function MissedForm({ job, onDone }) {
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [note, setNote] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    const s = parseTime(start), en = parseTime(end);
    if (s == null || en == null || en <= s) return toast.error("Use H:MM:SS or M:SS, end after start");
    if (en - s > 90) return toast.error("Clips max out at 90s — mark the tightest version of the moment");
    try {
      await axiosInstance.post("/admin/eval/missed", { jobId: job._id, start_sec: s, end_sec: en, note });
      toast.success("Missed moment saved");
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.error || "Save failed");
    }
  };
  return (
    <form className="cq-missed-form" onSubmit={submit}>
      <input placeholder="Start (1:02:13)" value={start} onChange={(e) => setStart(e.target.value)} autoFocus />
      <input placeholder="End (1:02:31)" value={end} onChange={(e) => setEnd(e.target.value)} />
      <input placeholder="What happens (optional)" value={note} onChange={(e) => setNote(e.target.value)} style={{ flex: 2 }} />
      <button className="ref-btn ref-btn--primary ref-btn--sm" type="submit">Save</button>
      <a className="muted" style={{ fontSize: 12 }} href={job.videoUrl} target="_blank" rel="noreferrer">open source ↗</a>
    </form>
  );
}

/* ── Paste links ───────────────────────────────────────────────────────── */

function PasteView({ onOpen }) {
  const [text, setText] = useState("");
  const [items, setItems] = useState(null);
  const resolve = async () => {
    try {
      const res = await axiosInstance.post("/admin/eval/resolve", { text });
      setItems(res.data.items);
    } catch (e) {
      toast.error(e.response?.data?.error || "Couldn't resolve links");
    }
  };
  const open = (it) => { pendingOpen = { jobId: it.jobId, clipId: it.clipId }; onOpen(); };
  return (
    <div>
      <div className="muted" style={{ fontSize: 13, marginBottom: 8 }}>
        Paste Clipbait clip links (storage.googleapis.com/…/clips/&lt;job&gt;/clip_N.mp4), job links, or job ids — one per line.
        Each opens on that exact clip so you can label your good/bad lists fast.
      </div>
      <textarea className="cq-note" rows={8} value={text} onChange={(e) => setText(e.target.value)} placeholder="https://storage.googleapis.com/clip-engine-media/clips/…/clip_2.mp4" />
      <div style={{ margin: "8px 0 14px" }}><button className="ref-btn ref-btn--primary" onClick={resolve} disabled={!text.trim()}>Find clips</button></div>
      {items && (
        <ul className="cq-missed-list">
          {items.map((it, i) => (
            <li key={i}>
              {it.error ? <span className="cq-bad">{it.error}</span> : (
                <button className="ref-btn ref-btn--sm" onClick={() => open(it)}>Label →</button>
              )}
              <span className="muted" style={{ marginLeft: 8 }}>{it.hook || it.input}</span>
              {!it.error && !it.clipId && <span className="muted"> (whole job)</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ── Quality & eval runs ───────────────────────────────────────────────── */

function QualityView() {
  const [stats, setStats] = useState(null);
  const [runs, setRuns] = useState(null);
  const [openRun, setOpenRun] = useState(null);
  const load = useCallback(async () => {
    try {
      const [s, r] = await Promise.all([axiosInstance.get("/admin/eval/stats"), axiosInstance.get("/admin/eval/runs")]);
      setStats(s.data);
      setRuns(r.data.runs);
    } catch (e) {
      toast.error(e.response?.data?.error || "Failed to load quality stats");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!stats) return <div className="admindash-empty admindash-shimmer">Loading…</div>;
  const tot = (o, v) => stats.totals.filter((t) => t.origin === o && (!v || t.verdict === v)).reduce((a, t) => a + t.n, 0);
  const good = tot("shipped", "good"), bad = tot("shipped", "bad");
  const failMax = Math.max(1, ...stats.failures.map((f) => f.n));
  const failLabel = Object.fromEntries(EVAL_FAILURES.map((f) => [f.key, f.label]));

  const showRun = async (id) => {
    try {
      const res = await axiosInstance.get(`/admin/eval/runs/${id}`);
      setOpenRun(res.data.run);
    } catch (e) {
      toast.error(e.response?.data?.error || "Failed to load run");
    }
  };

  const setBaseline = async (id) => {
    await axiosInstance.post(`/admin/eval/runs/${id}/baseline`);
    toast.success("Baseline set");
    load();
  };

  return (
    <div>
      <div className="cq-tiles">
        <Tile label="Shipped clips judged" value={good + bad} />
        <Tile label="Production precision" value={pct((good + bad) ? good / (good + bad) : null)} sub={`${good} good · ${bad} bad`} />
        <Tile label="Missed moments logged" value={stats.missed} />
        <Tile label="Eval sources" value={stats.sources.n} sub={`${stats.sources.full} fully labeled · ${stats.sources.frozen} frozen`} />
      </div>

      <div className="cq-panels">
        <div className="cq-panel">
          <div className="cq-panel-title">Production precision by week</div>
          <div className="muted cq-panel-sub">Share of labeled shipped clips judged good, by the week the job ran</div>
          {stats.weekly.length === 0 ? <div className="muted">No labels yet.</div> : (
            <div className="cq-cols" role="table" aria-label="Production precision by week">
              {stats.weekly.map((w) => (
                <div key={w.week} className="cq-col" role="row" title={`${w.week}: ${pct(w.precision)} good (${w.good} good / ${w.bad} bad)`}>
                  <div className="cq-col-val" role="cell">{pct(w.precision)}</div>
                  <div className="cq-col-track"><div className="cq-col-bar" style={{ height: `${Math.max(2, w.precision * 100)}%` }} /></div>
                  <div className="cq-col-label" role="cell">{w.week.slice(5)}</div>
                  <div className="cq-col-n muted">n={w.good + w.bad}</div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="cq-panel">
          <div className="cq-panel-title">Why clips fail</div>
          <div className="muted cq-panel-sub">Reasons on bad labels — fix the top one first</div>
          {stats.failures.length === 0 ? <div className="muted">No bad labels yet.</div> : stats.failures.map((f) => (
            <div key={f.key} className="cq-hbar" title={`${failLabel[f.key] || f.key}: ${f.n}`}>
              <div className="cq-hbar-label">{failLabel[f.key] || f.key}</div>
              <div className="cq-hbar-track"><div className="cq-hbar-fill" style={{ width: `${(f.n / failMax) * 100}%` }} /></div>
              <div className="cq-hbar-n">{f.n}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="cq-panel" style={{ marginTop: 14 }}>
        <div className="cq-panel-title">Production precision by genre</div>
        <div className="muted cq-panel-sub">Every core genre needs 3+ fully labeled VODs before its quality can be gated</div>
        {!stats.byGenre?.length ? <div className="muted">No labels yet.</div> : (
          <table className="cq-table">
            <thead><tr><th>Genre</th><th>VODs</th><th>Clips judged</th><th>Precision</th></tr></thead>
            <tbody>
              {[...stats.byGenre].sort((a, b) => b.sources - a.sources).map((g) => (
                <tr key={g.genre}>
                  <td>{genreLabel(g.genre)}</td>
                  <td>{g.sources}{g.sources < 3 && g.genre !== "unset" && <span className="muted"> (need 3+)</span>}</td>
                  <td>{g.good + g.bad}</td>
                  <td>{pct(g.precision)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="cq-panel" style={{ marginTop: 14 }}>
        <div className="cq-panel-title">Eval runs</div>
        <div className="muted cq-panel-sub">
          Each run re-picks clips on the frozen eval VODs and scores them against your labels. Precision = quality, recall = quantity,
          cut OK = start/end within 2s/3s of the right cut. A candidate ships only with an empty gate.
        </div>
        {!runs?.length ? <div className="muted">No runs yet — <code>modal run eval_engine.py</code> in clip-engine-runpod.</div> : (
          <table className="cq-table">
            <thead>
              <tr><th>Run</th><th>When</th><th>Split</th><th>Sources</th><th>Precision</th><th>Recall</th><th>Cut OK</th><th>Clips</th><th>Unlabeled</th><th>Gate</th><th /></tr>
            </thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r._id} className={r.isBaseline ? "cq-baseline" : ""}>
                  <td><button className="cq-linkbtn" onClick={() => showRun(r._id)}>{r.isBaseline && "★ "}{r.name || r.selectionHash}</button><div className="muted" style={{ fontSize: 11 }}>{r.selectionVersion} · {r.selectionHash}{r.repeat > 1 ? ` · ×${r.repeat}` : ""}</div></td>
                  <td>{new Date(r.createdAt).toLocaleString()}</td>
                  <td>{r.split === "holdout" ? "🔒 holdout" : r.split || "dev"}</td>
                  <td>{r.sources}</td>
                  <td>{pct(r.metrics?.precision)}<CI v={r.metrics?.precisionCI} /></td>
                  <td>{pct(r.metrics?.recall)}<CI v={r.metrics?.recallCI} /></td>
                  <td>{pct(r.metrics?.cutOk)}</td>
                  <td>{r.metrics?.clipsShipped}</td>
                  <td>{r.metrics?.unknown}</td>
                  <td>{r.gate?.length ? <span className="cq-bad" title={r.gate.join("\n")}>✗ {r.gate.length} issue{r.gate.length > 1 ? "s" : ""}</span> : <span className="cq-ok">✓ pass</span>}</td>
                  <td>{!r.isBaseline && <button className="ref-btn ref-btn--sm" onClick={() => setBaseline(r._id)}>Make baseline</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {openRun && <RunDetail run={openRun} onClose={() => setOpenRun(null)} />}
    </div>
  );
}

// One eval run, source by source: what it shipped, what it missed, and every
// clip no label covers yet — judge those right here so coverage stays high.
function RunDetail({ run, onClose }) {
  const [done, setDone] = useState({});
  const [picking, setPicking] = useState(null);
  const [fails, setFails] = useState([]);
  const judge = async (key, row, verdict, failures = []) => {
    try {
      await axiosInstance.post("/admin/eval/regions", {
        sourceKey: key, start_sec: row.start_sec, end_sec: row.end_sec, verdict, failures,
        previewUrl: row.previewUrl, runId: run._id, hook: row.hook,
      });
      setDone((d) => ({ ...d, [`${key}:${row.start_sec}`]: verdict }));
      setPicking(null);
      setFails([]);
    } catch (e) {
      toast.error(e.response?.data?.error || "Save failed");
    }
  };
  const m = run.metrics || {};
  return (
    <div className="cq-panel" style={{ marginTop: 14 }}>
      <div className="cq-missed-head">
        <div className="cq-panel-title">{run.name || run.selectionHash} · precision {pct(m.precision)} · recall {pct(m.recall)} · cut OK {pct(m.cutOk)}
          {m.consistency != null && ` · consistency ${pct(m.consistency)}`}</div>
        <button className="ref-btn ref-btn--sm" onClick={onClose}>Close</button>
      </div>
      {run.gate?.length > 0 && <div className="ref-warn">{run.gate.map((g) => <div key={g}>✖ {g}</div>)}</div>}
      {m.byGenre && (
        <table className="cq-table" style={{ marginBottom: 10 }}>
          <thead><tr><th>Genre</th><th>VODs</th><th>Precision</th><th>Recall</th><th>Cut OK</th><th>Clips</th></tr></thead>
          <tbody>
            {Object.entries(m.byGenre).map(([g, x]) => (
              <tr key={g}><td>{genreLabel(g)}</td><td>{x.sources}</td><td>{pct(x.precision)}</td><td>{pct(x.recall)}</td><td>{pct(x.cutOk)}</td><td>{x.clipsShipped}</td></tr>
            ))}
          </tbody>
        </table>
      )}
      {run.split === "holdout" && <div className="muted" style={{ fontSize: 12 }}>🔒 Holdout run — only clips that still need a label are listed; which clips passed or failed is hidden on purpose so nobody tunes on the final test.</div>}
      {Object.entries(run.perSource || {}).map(([key, r]) => (
        <div key={key} className="cq-run-source">
          <div className="cq-group-label">{key} — {run.split === "holdout" ? `${r.rows.length} to label (results hidden)` : <>{r.rows.filter((x) => x.kind === "hit").length} good · {r.rows.filter((x) => x.kind === "false_pos").length} bad · {r.rows.filter((x) => x.kind === "unknown").length} unlabeled · missed {r.missed.length}/{r.targets}</>}</div>
          {r.rows.map((x, i) => {
            const id = `${key}:${x.start_sec}`;
            return (
              <div key={i} className={`cq-run-row cq-run-row--${x.kind}`}>
                <span className="cq-run-kind">{{ hit: x.cutOk === false ? "≈" : "✔", false_pos: "✖", unknown: "?" }[x.kind]}</span>
                <span className="cq-run-time">{mmss(x.start_sec)}–{mmss(x.end_sec)}</span>
                <span className="cq-run-hook">{x.hook}</span>
                {x.kind === "hit" && x.cutOk != null && <span className="muted">cut {x.startErr > 0 ? "+" : ""}{x.startErr}s / {x.endErr > 0 ? "+" : ""}{x.endErr}s</span>}
                {x.kind === "false_pos" && <span className="cq-bad">{(x.failures || []).map((k) => EVAL_FAILURES.find((f) => f.key === k)?.label || k).join(", ")}</span>}
                {x.kind === "unknown" && (
                  <div className="cq-run-unknown">
                    {x.previewUrl ? <video src={x.previewUrl} controls preload="none" /> : <span className="muted">no preview</span>}
                    {done[id] ? <span className={done[id] === "good" ? "cq-ok" : "cq-bad"}>labeled {done[id]}</span> : (
                      <div className="cq-run-actions">
                        <button className="ref-btn ref-btn--sm" onClick={() => judge(key, x, "good")}>Good</button>
                        <button className="ref-btn ref-btn--sm" onClick={() => { setPicking(id); setFails([]); }}>Bad…</button>
                        {picking === id && (
                          <div className="cq-group">
                            {EVAL_FAILURES.map((f) => (
                              <button key={f.key} className={`cq-reason ${fails.includes(f.key) ? "on" : ""}`}
                                onClick={() => setFails((fs) => (fs.includes(f.key) ? fs.filter((k) => k !== f.key) : [...fs, f.key]))}>{f.label}</button>
                            ))}
                            <button className="ref-btn ref-btn--primary ref-btn--sm" disabled={!fails.length} onClick={() => judge(key, x, "bad", fails)}>Save bad</button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {r.missed.map((mm, i) => (
            <div key={`m${i}`} className="cq-run-row cq-run-row--missed">
              <span className="cq-run-kind">○</span><span className="cq-run-time">{mmss(mm.start_sec)}–{mmss(mm.end_sec)}</span>
              <span className="muted">missed — a labeled good moment this run didn't ship</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

// 90% interval from eval_score.bootstrap — if two runs' ranges overlap a lot, the difference may be luck.
function CI({ v }) {
  if (!v) return null;
  return <div className="muted" style={{ fontSize: 11 }}>{pct(v[0])}–{pct(v[1])}</div>;
}

function Tile({ label, value, sub }) {
  return (
    <div className="cq-tile">
      <div className="cq-tile-label">{label}</div>
      <div className="cq-tile-value">{value}</div>
      {sub && <div className="cq-tile-sub muted">{sub}</div>}
    </div>
  );
}
